import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  createStoreWebsiteDraftBrowseGrant,
  derivePublishAction,
  deriveWebsitePageRegions,
  findWebsiteEditRegion,
  getAdminStore,
  getWebsiteCanvasRenderDataV3,
  useWebsiteTemplateCatalogV3,
  revokeStoreWebsiteDraftBrowseGrant,
  websiteRegionForSlot,
  websiteTemplatesAdminKeys,
  type SupabaseClient,
  type WebsiteEditRegion,
  type WebsitePageKind,
} from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import { useStore } from '@/features/store-settings/use-store';
import { supabase } from '@/lib/supabase';
import {
  useStorefrontCanvasHost,
  type CanvasGrantSource,
  type CanvasPageData,
} from '../hooks/use-storefront-canvas-host';
import { useEditorLeaveGuard } from '../hooks/use-editor-leave-guard';
import { usePublishFlow } from '../hooks/use-publish-flow';
import { useTemplateDraftSaveQueue } from '../hooks/use-template-draft-save-queue';
import { useWebsiteT } from '../hooks/use-website-t';
import { resolveCanvasOrigins, type CanvasOrigins } from '../lib/admin-origins';
import { isLive, pageKindOfRegion, type ActiveEditorState } from '../lib/editor-state';
import { publishConfirmCopy, publishOutcome } from '../lib/publish-copy';
import type { FrameDevice } from '../lib/frame-viewport';
import { liveStoreUrl } from '../lib/live-store-address';
import { toCanvasRegionChrome } from '../lib/region-labels';
import { toCanvasStoreIdentity } from '../lib/store-identity';
import { TEMPLATE_LIBRARY_PATH } from '../lib/template-routes';
import { ConversionReportDialog } from './conversion-report-dialog';
import { EditorLeaveDialog } from './editor-leave-dialog';
import { FrameBrowserBar } from './frame-browser-bar';
import { FrameResetNotice } from './frame-status-states';
import { LegacyConversionNotice } from './legacy-conversion-notice';
import { PublishDialog } from './publish-dialog';
import { RegionEditDialog } from './region-edit-dialog';
import { StorefrontFrame } from './storefront-frame';
import { StorefrontToolbar } from './storefront-toolbar';
import { StoreLanguageDialog } from './store-language-dialog';
import { UpdateTemplateDialog } from './update-template-dialog';
import { WebsiteRuntimeNotice } from './website-runtime-notice';

export interface ActiveStorefrontScreenProps {
  readonly storeId: string;
  readonly storeSlug: string;
  /**
   * Epic #228 P3: the template of the edit route. Everything this screen
   * reads and writes names it; the store's compatibility draft pointer is
   * never read.
   */
  readonly templateId: string;
  readonly state: ActiveEditorState;
  /** Asks the server for this template's draft again. */
  readonly reloadState: () => Promise<unknown>;
  // Seams for tests. The defaults are the real client, the real origins, the real browser.
  readonly client?: SupabaseClient;
  readonly origins?: CanvasOrigins | null;
  readonly capturePoster?: (file: File) => Promise<File>;
  readonly openExternal?: (url: string) => void;
}

interface PendingCanvasAction {
  readonly regionId: string;
  /** The page to open first, or null to stay where the merchant is. */
  readonly pageKind: WebsitePageKind | null;
  /** "Go to item" turns edit mode on; returning from a dialog does not. */
  readonly ensureEditMode: boolean;
}

function defaultOpenExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * The active storefront (Screens 2 and 3): the merchant's real storefront on
 * the draft of one template, in a frame, with the controls of Dukanti around
 * it. The rendered template changes only when the route's template changes.
 *
 * - Edit mode on or off changes only the overlay of the canvas.
 * - A pencil opens one small dialog for one region. Epic #228 P4: every
 *   valid edit is auto-saved by this template's queue (one per template,
 *   shared by the dialogs, the toolbar, the publish flow and the other head
 *   writers); there is no Save button.
 * - While any dialog is open the canvas is locked (inert, no navigation).
 * - Publishing is a dialog: save first, check, fix, confirm, done.
 * - Leaving flushes the queue first and asks only when work would be lost.
 * There is no Save Draft, no Preview, no property panel.
 */
export function ActiveStorefrontScreen({
  storeId,
  storeSlug,
  templateId,
  state,
  reloadState,
  client = supabase,
  origins: originsProp,
  capturePoster,
  openExternal = defaultOpenExternal,
}: ActiveStorefrontScreenProps): React.JSX.Element {
  const { t, dir, lang } = useWebsiteT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { manifest, draft, head, template } = state;

  const [editMode, setEditMode] = useState(false);
  const [device, setDevice] = useState<FrameDevice>('desktop');
  const [openRegionId, setOpenRegionId] = useState<string | null>(null);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [pending, setPending] = useState<PendingCanvasAction | null>(null);
  const [blockedSignal, setBlockedSignal] = useState(0);

  const origins = useMemo(
    () =>
      originsProp !== undefined
        ? originsProp
        : resolveCanvasOrigins({
            currentOrigin: window.location.origin,
            publicAdminUrl: env.VITE_PUBLIC_ADMIN_URL,
            publicStorefrontUrl: env.VITE_PUBLIC_STOREFRONT_URL,
          }),
    [originsProp],
  );

  // Epic #228 P4: the auto-save queue of this template, and the leave guard on it.
  const { queue, snapshot } = useTemplateDraftSaveQueue({
    client,
    storeId,
    templateId,
    headRevisionId: head.revisionId,
    documentSlots: draft.document.slots,
  });
  const leave = useEditorLeaveGuard(queue, snapshot);

  // B1: the success of a design publish says when the store itself is offline.
  const store = useStore(storeId);
  const publish = usePublishFlow({ client, storeId, templateId, manifest, queue });

  // PD12: a save the editor state has not caught up with yet still differs
  // from what customers see, so a live template never flashes "All changes
  // published" between a save and the refetch that follows it.
  const savedAhead = Object.keys(snapshot.lastSaved).length > 0;
  const publishAction = derivePublishAction(
    savedAhead && !state.hasUnpublishedChanges ? { ...state, hasUnpublishedChanges: true } : state,
    snapshot,
  );

  const openRegion: WebsiteEditRegion | null = useMemo(
    () => (openRegionId === null ? null : findWebsiteEditRegion(manifest, openRegionId)),
    [manifest, openRegionId],
  );
  const locked =
    openRegion !== null || publish.stage !== 'closed' || languageOpen || reportOpen || updateOpen;

  const grants = useMemo<CanvasGrantSource>(
    () => ({
      mint: () => createStoreWebsiteDraftBrowseGrant(client, storeId),
      revoke: (token) => revokeStoreWebsiteDraftBrowseGrant(client, { storeId, token }),
    }),
    [client, storeId],
  );

  const loadPage = useCallback(
    async (pageKind: WebsitePageKind): Promise<CanvasPageData> => {
      const [render, store] = await Promise.all([
        // Under this template's canvas key, so a write to another template never touches it.
        queryClient.fetchQuery({
          queryKey: [
            ...websiteTemplatesAdminKeys.canvasV3(storeId, templateId, pageKind),
            head.revisionId,
          ],
          queryFn: () => getWebsiteCanvasRenderDataV3(client, { storeId, templateId, pageKind }),
          staleTime: 0,
        }),
        // Same key as `useStore`: the settings screen and this one share the row.
        queryClient.ensureQueryData({
          queryKey: ['store', storeId],
          queryFn: () => getAdminStore(client, storeId),
        }),
      ]);
      return { dto: render.dto, store: toCanvasStoreIdentity(store, env.VITE_SUPABASE_URL) };
    },
    [client, head.revisionId, queryClient, storeId, templateId],
  );

  const regionsFor = useCallback(
    (pageKind: WebsitePageKind) =>
      toCanvasRegionChrome(t, deriveWebsitePageRegions(manifest, pageKind)),
    [manifest, t],
  );
  const acceptsRegion = useCallback(
    (regionId: string, pageKind: WebsitePageKind) =>
      deriveWebsitePageRegions(manifest, pageKind).some((region) => region.regionId === regionId),
    [manifest],
  );

  const host = useStorefrontCanvasHost({
    storeSlug,
    origins,
    mode: 'store-draft',
    adminLocale: lang,
    overlayLabels: {
      moreItems: t('admin.websiteTemplates.overlay.moreItems'),
      regionListTitle: t('admin.websiteTemplates.overlay.regionListTitle'),
    },
    editMode,
    locked,
    contentKey: head.revisionId,
    loadPage,
    regionsFor,
    acceptsRegion,
    grants,
    onRegionEditIntent: (regionId) => setOpenRegionId(regionId),
    onNavigationBlocked: () => setBlockedSignal((count) => count + 1),
  });
  const { navigate: navigateFrame, focusRegion } = host;

  // What has to wait until the canvas is unlocked: a navigation is refused
  // while it is locked, and an inert marker cannot take focus.
  useEffect(() => {
    if (pending === null || locked) return;
    if (pending.ensureEditMode && !editMode) {
      setEditMode(true);
      return;
    }
    if (pending.pageKind !== null) navigateFrame({ kind: 'page', pageKind: pending.pageKind });
    if (editMode) focusRegion(pending.regionId);
    setPending(null);
  }, [pending, locked, editMode, navigateFrame, focusRegion]);

  // "Go to item" of a value the server refused: the same path as readiness "Go to item".
  const goToSlot = useCallback(
    (slotId: string): void => {
      const region = websiteRegionForSlot(manifest, slotId);
      if (region === null) return;
      setPending({
        regionId: region.regionId,
        pageKind: pageKindOfRegion(region),
        ensureEditMode: true,
      });
    },
    [manifest],
  );

  const closeRegion = useCallback((): void => {
    if (openRegionId !== null) {
      setPending({ regionId: openRegionId, pageKind: null, ensureEditMode: false });
    }
    setOpenRegionId(null);
  }, [openRegionId]);

  // What this publish does for shoppers. Another live template is named from the
  // catalog (shared with the library's cache); only read when one is live.
  const outcome = publishOutcome(state);
  const replacedTemplateId = outcome.kind === 'replace' ? outcome.liveTemplateId : null;
  const catalog = useWebsiteTemplateCatalogV3(client, {
    storeId: replacedTemplateId === null ? null : storeId,
    supabaseUrl: env.VITE_SUPABASE_URL,
  });
  const replacedTitleKey =
    catalog.data?.pages
      .flatMap((page) => page.items)
      .find((item) => item.templateId === replacedTemplateId)?.titleKey ?? null;
  const confirmCopy = publishConfirmCopy(
    t,
    outcome,
    t(template.titleKey),
    replacedTitleKey === null ? null : t(replacedTitleKey),
    store.data?.published,
  );

  // "View live store" only for a store that is actually public (the same store
  // `published` signal as the B1 success stage). Unknown while loading → off.
  // Otherwise the link opens "Store unavailable", the defect P1 fixed.
  const liveUrl = store.data?.published === true && isLive(state) ? liveStoreUrl(storeSlug) : null;
  const openLive = useCallback((): void => {
    if (liveUrl !== null) openExternal(liveUrl);
  }, [liveUrl, openExternal]);

  const showCartNote = host.route.pageKind === 'cart' || host.route.pageKind === 'checkout';

  return (
    <div
      dir={dir}
      data-testid="active-storefront"
      className="flex h-[calc(100dvh-3.5rem)] min-h-[32rem] min-w-0 flex-col sm:h-[calc(100dvh-4rem)]"
    >
      <StorefrontToolbar
        template={template}
        status={state.publicStatus === 'live' ? 'live' : 'not-live'}
        hasUnpublishedChanges={publishAction.label === 'publishChanges'}
        publishAction={publishAction}
        saveSnapshot={snapshot}
        onRetrySave={() => void queue.retry()}
        onReloadDraft={() => void queue.reload()}
        onGoToItem={goToSlot}
        saveStatusLive={openRegion === null}
        editMode={editMode}
        editableRegions={host.reportedRegions}
        editableRegionsKey={`${host.frameKey}:${host.route.path ?? ''}:${head.revisionId}:${lang}`}
        editingLocked={locked || host.status !== 'ready'}
        onEditRegion={host.editRegion}
        onEditModeChange={setEditMode}
        onBack={() => navigate('/dashboard')}
        onChangeTemplate={() => navigate(TEMPLATE_LIBRARY_PATH)}
        isPublishing={publish.stage === 'publishing'}
        onPublish={() => void publish.open()}
        onOpenLanguage={() => setLanguageOpen(true)}
        onOpenLive={openLive}
        liveUrl={liveUrl}
        onUpdateTemplate={state.updateAvailable ? () => setUpdateOpen(true) : undefined}
      />
      {template.kind === 'legacy-frozen' ? (
        <LegacyConversionNotice onViewReport={() => setReportOpen(true)} />
      ) : null}
      {host.resetNotice ? <FrameResetNotice onDismiss={host.dismissResetNotice} /> : null}
      {host.runtimeUnavailable && !locked ? (
        <div className="px-4 py-3" data-testid="active-storefront-runtime-recovery">
          <WebsiteRuntimeNotice storeId={storeId} />
        </div>
      ) : null}
      <FrameBrowserBar
        host={host}
        storeSlug={storeSlug}
        device={device}
        onDeviceChange={setDevice}
        liveUrl={liveUrl}
        onOpenLive={openLive}
      />
      {showCartNote ? (
        <p
          data-testid="draft-cart-note"
          className="border-border bg-admin-surface-muted text-muted-foreground border-b px-4 py-1.5 text-xs"
        >
          {t('admin.websiteTemplates.frame.draftCartNote')}
        </p>
      ) : null}
      <StorefrontFrame
        host={host}
        device={device}
        title={t('admin.websiteTemplates.gallery.title')}
      />

      {openRegion !== null ? (
        <RegionEditDialog
          key={openRegion.regionId}
          client={client}
          storeId={storeId}
          templateId={templateId}
          manifest={manifest}
          document={draft.document}
          region={openRegion}
          queue={queue}
          snapshot={snapshot}
          attentionSignal={blockedSignal}
          runtimeUnavailable={host.runtimeUnavailable}
          capturePoster={capturePoster}
          device={device}
          onClose={closeRegion}
        />
      ) : null}

      <PublishDialog
        flow={publish}
        storePublished={store.data?.published}
        confirmCopy={confirmCopy}
        onGoToItem={(region, issuePageKind) => {
          publish.close();
          setPending({
            regionId: region.regionId,
            pageKind: issuePageKind ?? pageKindOfRegion(region),
            ensureEditMode: true,
          });
        }}
        onGoToSettings={() => {
          publish.close();
          navigate('/dashboard/settings');
        }}
        onGoToStorePublish={() => {
          // The store publish card and its checks live on the dashboard.
          publish.close();
          navigate('/dashboard');
        }}
        onViewLive={() => {
          openLive();
          publish.close();
        }}
      />

      {languageOpen ? (
        <StoreLanguageDialog
          client={client}
          storeId={storeId}
          templateId={templateId}
          current={draft.document.interfaceLocale}
          queue={queue}
          reloadState={reloadState}
          onClose={() => setLanguageOpen(false)}
        />
      ) : null}
      {updateOpen ? (
        <UpdateTemplateDialog
          client={client}
          storeId={storeId}
          templateId={templateId}
          queue={queue}
          reloadState={reloadState}
          onClose={() => setUpdateOpen(false)}
        />
      ) : null}
      <EditorLeaveDialog guard={leave} />
      {reportOpen ? (
        <ConversionReportDialog
          client={client}
          storeId={storeId}
          onClose={() => setReportOpen(false)}
        />
      ) : null}
    </div>
  );
}
